import { Funnel, MagnifyingGlass } from "@phosphor-icons/react";
import { Children, forwardRef } from "react";
import styled from "@emotion/styled";
import { Badge } from "@/components/primitives/Badge";
import { Button } from "@/components/primitives/Button";
import { Chip } from "@/components/primitives/Chip";
import { Input } from "@/components/primitives/Input";
import type { FilterBarProps } from "./FilterBar.types";

/* Exact compiled output of the pre-migration Tailwind utilities, in the same
 * class-list emission order. Verified against the frozen baseline. */

const FilterBarRoot = styled("div", {
  label: "AgenticFilterBar-root",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { sticky: boolean } }>(({ ownerState }) => [
  {
    borderBottomWidth: "1px",
    "--tw-border-opacity": "1",
    borderColor: "hsl(var(--bfrs-color-border) / var(--tw-border-opacity, 1))",
    "--tw-bg-opacity": "1",
    // Themable surface: hosts (e.g. ListPageLayout's filter band) set
    // --bfrs-filterbar-bg to blend the bar with their own sheet. The fallback
    // is the exact pre-existing expression.
    backgroundColor: "var(--bfrs-filterbar-bg, rgb(251 252 251 / var(--tw-bg-opacity, 1)))"
  },
  ownerState.sticky && {
    position: "sticky",
    top: 0,
    zIndex: 30
  }
]);

const FilterBarRow = styled("div", {
  label: "AgenticFilterBar-row"
})({
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: ".75rem",
  paddingLeft: "1.25rem",
  paddingRight: "1.25rem",
  paddingTop: ".625rem",
  paddingBottom: ".625rem"
});

/* `&&` reproduces the old tailwind-merge resolution deterministically: the
 * responsive width must beat the Input root's own `width:100%`. */
const FilterBarSearch = styled(Input, {
  label: "AgenticFilterBar-search"
})({
  "&&": {
    width: "100%",
    "@media (min-width:640px)": {
      width: "16rem"
    }
  }
});

const FilterBarCountBadge = styled(Badge, {
  label: "AgenticFilterBar-countBadge"
})({
  marginLeft: ".25rem"
});

const FilterBarSpacer = styled("div", {
  label: "AgenticFilterBar-spacer"
})({
  minWidth: ".5rem",
  flex: "1 1 0%"
});

const FilterBarChipsRow = styled("div", {
  label: "AgenticFilterBar-chipsRow"
})({
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: ".5rem",
  borderTopWidth: "1px",
  "--tw-border-opacity": "1",
  borderColor: "hsl(var(--bfrs-color-border) / var(--tw-border-opacity, 1))",
  backgroundColor: "hsl(var(--bfrs-color-surface-muted) / .5)",
  paddingLeft: "1.25rem",
  paddingRight: "1.25rem",
  paddingTop: ".5rem",
  paddingBottom: ".5rem"
});

export const FilterBar = forwardRef<HTMLDivElement, FilterBarProps>(
  (
    {
      className,
      searchValue,
      onSearchChange,
      onClearSearch,
      searchPlaceholder = "Search",
      searchInput,
      filterTrigger,
      onOpenFilters,
      activeFiltersCount = 0,
      filterChips = [],
      onClearAllFilters,
      primaryAction,
      secondaryActions,
      sticky,
      rowClassName,
      chipsRowClassName,
      spacer = true,
      children,
      ...props
    },
    ref
  ) => {
    // The row is a *filter* section: render it only when it holds a real filter
    // affordance — search, inline filter controls (Select/MultiSelect/Input passed
    // as children), or the "More Filters" trigger. Standalone action buttons
    // (primaryAction / secondaryActions) do NOT keep an otherwise-empty filter row
    // visible.
    const hasFilterControls =
      Boolean(searchInput) ||
      Boolean(onSearchChange) ||
      Boolean(filterTrigger) ||
      Boolean(onOpenFilters) ||
      Children.toArray(children).length > 0;
    const hasChips = filterChips.length > 0 || activeFiltersCount > 0;

    // Nothing filter-related to show → omit the bar entirely rather than leave an
    // empty bordered strip.
    if (!hasFilterControls && !hasChips) return null;

    return (
      <FilterBarRoot ref={ref} ownerState={{ sticky: Boolean(sticky) }} className={className} {...props}>
        {hasFilterControls && (
          <FilterBarRow className={rowClassName}>
            {searchInput ??
              (onSearchChange && (
                <FilterBarSearch
                  size="sm"
                  value={searchValue ?? ""}
                  onChange={(event) => onSearchChange(event.target.value)}
                  onClear={onClearSearch}
                  placeholder={searchPlaceholder}
                  leftIcon={<MagnifyingGlass size={14} />}
                  aria-label={searchPlaceholder}
                />
              ))}
            {children}
            {filterTrigger ??
              (onOpenFilters && (
                <Button variant="secondary" size="sm" leftIcon={<Funnel size={13} />} onClick={onOpenFilters}>
                  More Filters
                  {activeFiltersCount > 0 && <FilterBarCountBadge tone="primary" count={activeFiltersCount} />}
                </Button>
              ))}
            {spacer && <FilterBarSpacer />}
            {secondaryActions}
            {primaryAction}
          </FilterBarRow>
        )}
        {hasChips && (
          <FilterBarChipsRow className={chipsRowClassName}>
            {filterChips.map((chip) => (
              <Chip key={chip.id} tone="neutral" onRemove={chip.onRemove}>
                {chip.label}
              </Chip>
            ))}
            {filterChips.length === 0 && activeFiltersCount > 0 && <Chip tone="neutral">{activeFiltersCount} filters applied</Chip>}
            {onClearAllFilters && (
              <Button variant="link" size="sm" onClick={onClearAllFilters}>
                Clear all
              </Button>
            )}
          </FilterBarChipsRow>
        )}
      </FilterBarRoot>
    );
  }
);

FilterBar.displayName = "FilterBar";
